import { useMemo } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';

import { type PartialLineItem } from '../../../../graphql';

/**
 * Excludes products that are missing required fields and formats custom product names.
 */
export const useValidProducts = (lineItems: readonly PartialLineItem[]): Product[] => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    return lineItems
      ?.map((lineItem) => {
        const product = lineItem?.product;
        const name = product?.name;

        const isCustomized = Boolean(lineItem?.isCustom && !product?.isCustom);
        const customizedName = formatMessage(messages.customName, { name });

        return {
          id: lineItem.product.id,
          name: isCustomized ? customizedName : lineItem.product.name,
          quantity: lineItem.quantity,
          imageUrl: lineItem.product.asset?.url,
        };
      })
      ?.filter(
        (product) => product.id && product.name && product.quantity && product.imageUrl,
      ) as Product[];
  }, [lineItems, formatMessage]);
};

// ─── Messages ────────────────────────────────────────────────────────────────

export const messages = defineMessages<{ readonly customName: { readonly name: MessageValue } }>({
  customName: {
    defaultMessage: 'Custom {name}',
    description: 'Bag Screen | Custom product name format',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Product = {
  id: string;
  name: string;
  quantity: number;
  imageUrl: string;
};
